/*
  Configuração do Vue Router para o projeto aquare!a.

  - Define todas as rotas da aplicação, incluindo lazy loading dos componentes de página.
  - Implementa proteção de rotas (requiresAuth) para páginas que exigem autenticação.
  - Redireciona usuários já autenticados que tentam acessar a página de login.
  - Impede navegação se houver rascunhos pendentes no localStorage, exceto para a rota de novo REA.
  - Exibe modal de aviso ao tentar sair com rascunho pendente.
  - Força atualização da página ao navegar entre diferentes cards ou rascunhos.
  - (Comentado) Possui opção para controlar o scroll ao trocar de rota.
*/

import { createRouter, createWebHistory } from 'vue-router'
import { useUserStore } from '@/utils/stores/userStore'
import i18n from '@/i18n/i18n'
import { showModal } from '@/utils/eventBus'

// Definição de todas as rotas do projeto
const routes = [
  {
    path: '/',
    name: 'home',
    component: () => import(/* webpackChunkName: "home" */ '../views/HomeView.vue'),
    meta: { title: '[ home ]' }
  },
  {
    path: '/pesquisa',
    name: 'pesquisa',
    component: () => import(/* webpackChunkName: "pesquisa" */ '../views/SearchView.vue'),
    meta: { title: '[ pesquisa ]' }
  },
  {
    path: '/login',
    name: 'login',
    component: () => import(/* webpackChunkName: "login" */ '../views/LoginView.vue'),
    meta: { title: '[ login ]' }
  },
  {
    path: '/sobrenos',
    name: 'sobrenos',
    component: () => import(/* webpackChunkName: "sobrenos" */ '../views/AboutUsView.vue'),
    meta: { title: '[ sobre nós ]' }
  },
  {
    path: '/ajuda',
    name: 'ajuda',
    component: () => import(/* webpackChunkName: "ajuda" */ '../views/ContactUsView.vue'),
    meta: { title: '[ ajuda ]' }
  },
  {
    path: '/tutorial',
    name: 'tutorial',
    component: () => import(/* webpackChunkName: "tutorial" */ '../views/TutorialView.vue'),
    meta: { title: '[ tutorial ]' }
  },
  {
    path: '/colaborador/:id',
    name: 'colaborador',
    props: true,
    component: () => import(/* webpackChunkName: "colaborador" */ '../views/ProfileView.vue'),
    meta: { title: '[ colaborador ]' }
  },
  {
    path: '/dadosperfil',
    name: 'dadosperfil',
    component: () => import(/* webpackChunkName: "dadosperfil" */ '../views/ProfileDataView.vue'),
    meta: { title: '[ dados do perfil ]' }
  },
  {
    path: '/rea/:id',
    name: 'rea',
    props: true,
    component: () => import(/* webpackChunkName: "rea" */ '../views/CardView.vue'),
    meta: { title: '[ rea ]' },
    /* para garantir navegação correta quando cards de uma trilha de aprendizagem são acessados */
    beforeEnter: (to, from, next) => {
      if (to.params.id !== from.params.id) {
        to.meta.forceRefreshKey = Date.now();
      }
      next();
    }  
  },
  {
    path: '/rascunho/:id',
    name: 'rascunho',
    props: true,
    component: () => import(/* webpackChunkName: "rascunho" */ '../views/DraftPostPreviewView.vue'),
    meta: { title: '[ rascunho ]' }
  },
  {
    path: '/novorea',
    name: 'novorea',
    component: () => import(/* webpackChunkName: "novorea" */ '../views/CreateREA/NewREAView.vue'),
    meta: { requiresAuth: true, title: '[ novo REA ]' }  // marca a rota como protegida
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import(/* webpackChunkName: "not-found" */ '../views/NotFoundView.vue'),
    meta: { title: '[ not found ]' }
  },
  {
    path: '/novacomunidade',
    name: 'novacomunidade',
    component: () => import(/* webpackChunkName: "novacomunidade" */ '../views/FormsComunidadeView.vue'),
    meta: { requiresAuth: true, title: '[ nova comunidade ]' }  // marca a rota como protegida
  },

  {
    path: '/comunidade/:id',
    name: 'comunidade',
    props: true,
    component: () => import(/* webpackChunkName: "comunidade" */ '../views/ComunidadeView.vue'),
    meta: { title: '[ comunidade ]' },
    children: [
      { path: '', redirect: { name: 'comunidade-inicio' } },
      { 
        path: 'inicio', 
        name: 'comunidade-inicio',
        component: () => import('../views/ComunidadeView.vue'),
        meta: { title: '[ comunidade - início ]' },
      },
      { 
        path: 'rea', 
        name: 'comunidade-rea',
        component: () => import('../views/ComunidadeView.vue'),
        meta: { title: '[ comunidade - REA ]' },
      },
      { 
        path: 'membros', 
        name: 'comunidade-membros',
        component: () => import('../views/ComunidadeView.vue'),
        meta: { title: '[ comunidade - membros ]' }, 
      },
      { 
        path: 'forum', 
        name: 'comunidade-forum',
        component: () => import('../views/ComunidadeView.vue'),
        meta: { title: '[ comunidade - fórum ]' }, 
      },
      { 
        path: 'forum/:topicId', 
        name: 'comunidade-forum-topico',
        component: () => import('../views/ComunidadeForumTopicView.vue'),
        meta: { title: '[ comunidade - fórum - tópicos ]' }, 
      },
    ]
  },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // se o usuário usou "voltar", mantém posição salva
    if (savedPosition) {
      return { ...savedPosition, behavior: 'auto' }
    } else {
      // sempre sobe para o topo em novas navegações
      return { top: 0, behavior: 'auto' }
    }
  }
})

router.beforeEach((to, from, next) => {
  // Impede navegação se houver rascunho pendente, exceto para /novorea
  const hasDraft = Object.keys(localStorage).some(key => key.startsWith('draft'))
  
  if (hasDraft) {
    // se estiver no modal de busca para criação de uma trilha, ignora mensagem sobre rascunho pendente
    if (from.name === 'novorea' && to.name === 'pesquisa')
      return next(false);
    else
    // se estiver na página de criação de uma trilha e um card é clicado para que o usuário visualize o conteúdo no modal  
    if (from.name === 'novorea' && to.name === 'rea')
      return next();
    else
    if (to.name !== 'novorea') {
      showModal(
        i18n.global.t('tinyEditor.aviso'),
        i18n.global.t('navbar.rascunhoPendente'),
        '/novorea'
      )
      return next(false)
    }
  }

  // tornar userStore disponível
  const userStore = useUserStore()

  // Bloqueia pesquisa e home se o usuário estiver dentro de uma comunidade
  if (userStore.communityId && (to.path === '/pesquisa' || to.path === '/')) {
    return next(`/comunidade/${userStore.communityId}/inicio`)
  }

  // Redireciona usuário autenticado que tenta acessar login
  if (to.name === 'login' && userStore.email) {
    return next('/')
  }

  // Bloqueia acesso a rotas protegidas se não autenticado
  if (to.meta.requiresAuth && !userStore.email) {
    return next('/')
  }

  next()
})

export default router